"use client";

/**
 * Games v2 on the website: the server's clock, and the time words every games
 * screen shares.
 *
 * Every countdown (the round clock, "Ends in 6d 4h", the waiting room) runs on
 * the SERVER's clock, never the phone's own (GAMES_V2_GUIDE.md §5). There is
 * one offset for the whole page: any response carrying `serverNow`
 * (startPlay, joinWaitingRoom, ping) sets it through `sync`, and every screen
 * reads it through `now()`.
 *
 * Times are shown in Kampala time (UTC+3 all year), whatever the phone's zone.
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { ServerClock } from "./types";

// ------------------------------------------------------------------ the clock

/** A timed answer older than this no longer beats a new, slower one. */
const FRESH_MS = 5 * 60_000;

let offsetMs = 0;
let synced = false;
let lastRttMs = Infinity;
let lastSyncAt = 0;
const listeners = new Set<() => void>();

/** The server's time now, in ms since the epoch. */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

/**
 * Set the offset from a response's `serverNow`. Pass `sentAt` (Date.now()
 * just before the call) when you have it: the answer's travel time is then
 * split in two, and a quick answer is kept over a much slower one.
 */
export function syncServerClock(serverNowMs: number, sentAt?: number): void {
  if (typeof serverNowMs !== "number" || !Number.isFinite(serverNowMs)) return;
  const receivedAt = Date.now();
  const timed = typeof sentAt === "number" && Number.isFinite(sentAt) && sentAt <= receivedAt;
  const rtt = timed ? receivedAt - (sentAt as number) : Infinity;
  const fresh = synced && receivedAt - lastSyncAt < FRESH_MS;
  if (fresh) {
    // An untimed answer never replaces a timed one; a slow answer never
    // replaces a quick one.
    if (!timed && lastRttMs < Infinity) return;
    if (timed && rtt > lastRttMs * 2 + 250) return;
  }
  offsetMs = serverNowMs - (timed ? receivedAt - rtt / 2 : receivedAt);
  lastRttMs = rtt;
  lastSyncAt = receivedAt;
  synced = true;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The shared clock, for code outside React. */
export const serverClock: ServerClock = { now: serverNow };

export interface ServerClockHandle extends ServerClock {
  /** Set the offset from any response carrying `serverNow` (see syncServerClock). */
  sync(serverNow: number, sentAt?: number): void;
  /** True once a server answer has set the clock. */
  synced: boolean;
}

/** The server's clock: `now()` for countdowns, `sync(serverNow)` after any answer that carries it. */
export function useServerClock(): ServerClockHandle {
  const isSynced = useSyncExternalStore(
    subscribe,
    () => synced,
    () => false
  );
  return useMemo(() => ({ now: serverNow, sync: syncServerClock, synced: isSynced }), [isSynced]);
}

/**
 * The server's time, re-read every `intervalMs` (and when the page comes back
 * to the front, or the clock is set). `null` until the page is on screen, so
 * the server's HTML and the browser's first paint agree.
 */
export function useNow(intervalMs = 1000): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    const tick = () => setNow(serverNow());
    tick();
    const id = window.setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    listeners.add(tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      listeners.delete(tick);
    };
  }, [intervalMs]);
  return now;
}

/** The server's time rounded down to the minute: the `now` the games queries take. */
export function floorToMinute(ms: number): number {
  return Math.floor(ms / 60_000) * 60_000;
}

/**
 * The server's minute, moving on at each minute boundary (for `getGame({ now })`).
 * `null` until the page is on screen (nothing reads the clock while rendering).
 */
export function useMinute(): number | null {
  const [minute, setMinute] = useState<number | null>(null);
  useEffect(() => {
    let timer = 0;
    const schedule = () => {
      const now = serverNow();
      setMinute(floorToMinute(now));
      timer = window.setTimeout(schedule, 60_000 - (now % 60_000) + 50);
    };
    schedule();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      window.clearTimeout(timer);
      schedule();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  return minute;
}

// ------------------------------------------------------------- Kampala time

export const KAMPALA_TZ = "Africa/Kampala";
const KAMPALA_OFFSET_MS = 3 * 3_600_000;
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const fmtDate = new Intl.DateTimeFormat("en-GB", { timeZone: KAMPALA_TZ, weekday: "short", day: "numeric", month: "short" });
const fmtDateNoWeekday = new Intl.DateTimeFormat("en-GB", { timeZone: KAMPALA_TZ, day: "numeric", month: "short" });
const fmtWeekday = new Intl.DateTimeFormat("en-GB", { timeZone: KAMPALA_TZ, weekday: "short" });
const fmtTime = new Intl.DateTimeFormat("en-US", { timeZone: KAMPALA_TZ, hour: "numeric", minute: "2-digit" });

/** One kind of space everywhere (browsers and Node disagree on the one before "PM"). */
function tidy(s: string): string {
  return s.replace(/[  ]/g, " ");
}

/** "Mon 12 Oct" (or "12 Oct" without the weekday), in Kampala. */
export function kampalaDate(ms: number, withWeekday = true): string {
  return tidy((withWeekday ? fmtDate : fmtDateNoWeekday).format(ms));
}

/** "8:00 PM", in Kampala. */
export function kampalaTime(ms: number): string {
  return tidy(fmtTime.format(ms));
}

/** "Mon 12 Oct, 11:59 PM", in Kampala. */
export function kampalaDateTime(ms: number): string {
  return `${kampalaDate(ms)}, ${kampalaTime(ms)}`;
}

/** The Kampala calendar day of a moment, "2026-10-12". */
export function kampalaDay(ms: number): string {
  return new Date(ms + KAMPALA_OFFSET_MS).toISOString().slice(0, 10);
}

/** Is this moment exactly Kampala midnight? */
function isMidnight(ms: number): boolean {
  return (ms + KAMPALA_OFFSET_MS) % DAY === 0;
}

/**
 * A moment said the short way, relative to now: "Today 8:00 PM",
 * "Tomorrow 8:00 PM", "Fri 8:00 PM" (this week), else "Mon 6 Oct, 9:00 AM".
 */
export function kampalaWhen(ms: number, now: number): string {
  const days = Math.round((Date.parse(kampalaDay(ms)) - Date.parse(kampalaDay(now))) / DAY);
  if (days === 0) return `Today ${kampalaTime(ms)}`;
  if (days === 1) return `Tomorrow ${kampalaTime(ms)}`;
  if (days > 1 && days < 7) return `${tidy(fmtWeekday.format(ms))} ${kampalaTime(ms)}`;
  return kampalaDateTime(ms);
}

/** "Next play at midnight" / "Next play at 3:00 PM" / "Next play on Tue 14 Oct, 9:00 AM". */
export function nextPlayLine(nextPlayAt: number, now: number): string {
  const days = Math.round((Date.parse(kampalaDay(nextPlayAt)) - Date.parse(kampalaDay(now))) / DAY);
  if (isMidnight(nextPlayAt) && days <= 1) return "Next play at midnight";
  if (days === 0) return `Next play at ${kampalaTime(nextPlayAt)}`;
  if (days === 1) return `Next play tomorrow at ${kampalaTime(nextPlayAt)}`;
  return `Next play on ${kampalaDateTime(nextPlayAt)}`;
}

/** Time left in short words: "6d 4h", "5h 12m", "12m", "45s". */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return "0s";
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / HOUR);
  const m = Math.floor((ms % HOUR) / MINUTE);
  const s = Math.floor((ms % MINUTE) / 1000);
  if (d >= 1) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h >= 1) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m >= 1) return `${m}m`;
  return `${s}s`;
}

/** A round clock: "04:12", or "1:04:12" past an hour. Rounds up, so it reads 00:00 only at the end. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** "Ends today" / "Ends tomorrow" / "Ends in 5 days" / "Ended", counted in Kampala days. */
export function endsInDays(endsAt: number, now: number): { text: string; days: number } {
  if (endsAt <= now) return { text: "Ended", days: -1 };
  const days = Math.round((Date.parse(kampalaDay(endsAt)) - Date.parse(kampalaDay(now))) / DAY);
  if (days <= 0) return { text: "Ends today", days: 0 };
  if (days === 1) return { text: "Ends tomorrow", days: 1 };
  return { text: `Ends in ${days} days`, days };
}

/** "1st", "2nd", "3rd", "11th", "22nd". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

// ------------------------------------------------------------------ motion

const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";

function readReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(REDUCED_QUERY).matches;
}

/** The shopper asked their phone for less motion: shorter, gentler animations. */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    (listener) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
      const query = window.matchMedia(REDUCED_QUERY);
      query.addEventListener("change", listener);
      return () => query.removeEventListener("change", listener);
    },
    readReducedMotion,
    () => false
  );
}
